/**
 * Pure helpers used by ChatbotAnalyticsReportPage.
 *
 * Kept in a separate module so they're trivially unit-testable without
 * pulling in Redux, the router or the API client.
 */

export const ENDPOINT = 'office/chatbot-analytics-report'
export const EXPORT_ENDPOINT = 'office/chatbot-analytics-report/export'

/**
 * Which chatbot the numbers describe. Mirrors `\Api\Enum\Chatbot\ChatbotSource`.
 *
 * The report covers one source at a time, because the conversion bot and the profile
 * chatbot run different funnels. There is no "all" option on purpose: it would fold the
 * setup wizard's own preview traffic into the totals.
 */
export type ChatbotReportSource = 'conversion_bot' | 'profile_chatbot' | 'preview'

export const DEFAULT_SOURCE: ChatbotReportSource = 'conversion_bot'

export const SOURCE_OPTIONS: Array<{ value: ChatbotReportSource; label: string }> = [
    { value: 'conversion_bot', label: 'Conversion bot (provider website)' },
    { value: 'profile_chatbot', label: 'Profile chatbot (Trustoo profile pages)' },
    { value: 'preview', label: 'Preview (dashboard setup wizard)' },
]

export const sourceLabel = (source: ChatbotReportSource): string =>
    SOURCE_OPTIONS.find((option) => option.value === source)?.label ?? source

/**
 * Build a query string for the analytics endpoints from the current filters.
 *
 * - Empty / nullish values are dropped (the controller will fall back to its
 *   "current month" / "all countries" / "all providers" defaults).
 * - Country is left as-is (the controller uppercases it server-side).
 * - Source is always sent, so the URL states which chatbot the numbers describe rather
 *   than leaning on the server default.
 */
export const buildQueryString = (
    startDate: string,
    endDate: string,
    country: string | null,
    providerId: string,
    source: ChatbotReportSource = DEFAULT_SOURCE,
): string => {
    const params = new URLSearchParams()
    if (startDate !== '') {
        params.set('start_date', startDate)
    }
    if (endDate !== '') {
        params.set('end_date', endDate)
    }
    if (country !== null && country !== '') {
        params.set('country', country)
    }
    if (providerId !== '') {
        params.set('provider_id', providerId)
    }
    params.set('source', source)
    return params.toString()
}

const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export const defaultStartDate = (): string => {
    const now = new Date()
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

export const defaultEndDate = (): string => {
    const now = new Date()
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
}

export const formatPercent = (value: number): string => `${value.toFixed(1)}%`
